import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, LinearProgress } from '@mui/material';
import Navigation from './components/Navigation';
import AppHeader from './components/layout/AppHeader';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ExportPage from './pages/ExportPage';
import LabelPage from './pages/LabelPage';
import MappingDocumentPage from './pages/MappingDocumentPage';
import SwaggerFilesPage from './pages/SwaggerFilesPage';
import DataArchitecturePage from './pages/DataArchitecturePage';
import AuthService from './services/authService';

function App() {
  const [authState, setAuthState] = useState(AuthService.getInstance().getAuthState());
  const [isInitializing, setIsInitializing] = useState(true);
  const authService = AuthService.getInstance();

  useEffect(() => {
    // Listen for auth state changes
    const checkAuth = async () => {
      const currentAuthState = authService.getAuthState();
      
      // Check if token has expired
      if (currentAuthState.isAuthenticated && authService.isTokenExpired()) {
        console.log('Token expired, redirecting to login');
        authService.forceLogout();
        setAuthState(authService.getAuthState());
        setIsInitializing(false);
        return;
      }
      
      setAuthState(currentAuthState);
      setIsInitializing(false);
    };

    // Check auth state on mount
    checkAuth();

    // Set up interval to check auth state and token expiration
    const interval = setInterval(checkAuth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [authService]);

  const handleLogout = useCallback(() => {
    authService.logout();
    setAuthState(authService.getAuthState());
  }, [authService]);

  // const selectedPartner = authState.selectedPartner;

  // Show loading screen while initializing
  if (isInitializing) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          position: 'relative',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Initializing Shipium Partner Assistant...
        </Typography>
        <LinearProgress sx={{ width: '300px', mt: 2 }} />
        
        {/* Copyright Footer */}
        <Box 
          component="footer" 
          sx={{ 
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            py: 2,
            textAlign: 'center',
            backgroundColor: 'transparent',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Copyright Shipium, Inc. © {new Date().getFullYear()}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!authState.isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navigation />
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppHeader username={authState.user?.securityInfo?.username || 'User'} onLogout={handleLogout} />
        <Box component="main" sx={{ flex: 1, px: 4, pt: 3.5, pb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/export" element={
                <ProtectedRoute>
                  <ExportPage />
                </ProtectedRoute>
              } />
              <Route path="/label" element={
                <ProtectedRoute>
                  <LabelPage />
                </ProtectedRoute>
              } />
              <Route path="/architecture" element={
                <ProtectedRoute>
                  <DataArchitecturePage />
                </ProtectedRoute>
              } />
              <Route path="/mapping-document" element={
                <ProtectedRoute>
                  <MappingDocumentPage />
                </ProtectedRoute>
              } />
              <Route path="/swagger-files" element={
                <ProtectedRoute>
                  <SwaggerFilesPage />
                </ProtectedRoute>
              } />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Box>
          <Box component="footer" sx={{ textAlign: 'center', fontSize: 12, color: 'text.secondary', pt: 1 }}>
            © {new Date().getFullYear()} Shipium, Inc.
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default App;
